import React, { useEffect, useRef, useState, useCallback } from 'react';
import { getCookie, clearAllCookies } from '../helpers/cookie';
import { getRefreshToken } from '../helpers/auth';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { jwtDecode } from 'jwt-decode';
import { useDispatch } from 'react-redux';

const SessionManager = ({ children }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const rootRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  const dispatch = useDispatch();

  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  };

  // Check token expiration and refresh if needed
  //  const checkTokenExpiration = useCallback(async () => {
  //     const accessToken = getCookie('accessToken');
  //     if (!accessToken) return;

  //     try {
  //       const decoded = jwtDecode(accessToken);
  //       if (!decoded.exp) return;

  //       const expirationTime = decoded.exp * 1000;
  //       const currentTime = Date.now();
  //       const timeRemaining = expirationTime - currentTime;
  //       const fiveMinutesInMs = 5 * 60 * 1000;

  //       console.log('Current time:', new Date(currentTime).toLocaleString());
  //       console.log('Token expires at:', new Date(expirationTime).toLocaleString());
  //       console.log('Time remaining (ms):', timeRemaining);

  //       if (timeRemaining <= fiveMinutesInMs && timeRemaining > 0) {
  //         // console.log('Token expiring soon, refreshing...');
  //         dispatch(getRefreshToken());
  //       }
  //     } catch (error) {
  //       console.error('Error checking token expiration:', error);
  //     }
  //   }, [dispatch]);
  const checkTokenExpiration = useCallback(async () => {
    const refreshToken = getCookie('refreshToken');
    if (!refreshToken) return; // User is not logged in

    const accessToken = getCookie('accessToken');
    let shouldRefresh = false;

    if (!accessToken) {
      // Access token has expired/been cleared, but refresh token is still present.
      shouldRefresh = true;
    } else {
      try {
        const decoded = jwtDecode(accessToken);
        if (decoded.exp) {
          const expirationTime = decoded.exp * 1000;
          const currentTime = Date.now();
          const timeRemaining = expirationTime - currentTime;
          const fiveMinutesInMs = 5 * 60 * 1000;

          if (timeRemaining <= fiveMinutesInMs) {
            shouldRefresh = true;
          }
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
        shouldRefresh = true;
      }
    }

    if (shouldRefresh) {
      const lastRefresh = localStorage.getItem('lastTokenRefresh');
      if (lastRefresh && Date.now() - parseInt(lastRefresh, 10) < 30000) {
        return; // Another tab is currently refreshing the token
      }
      localStorage.setItem('lastTokenRefresh', Date.now().toString());

      try {
        const response = await getRefreshToken();
        if (!response) {
          window.location.href = '/login';
        }
      } catch (error) {
        console.error('Error refreshing token in SessionManager:', error);
      }
    }
  }, []);

  // Track user activity and check token on activity
  useEffect(() => {
    let lastUpdate = 0;
    const updateActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      localStorage.setItem('lastActivity', now.toString());

      // Throttle checking token expiration on high-frequency events to once every 15s
      if (now - lastUpdate > 15000) {
        lastUpdate = now;
        checkTokenExpiration();
      }
    };

    const events = [
      'click',
      'dblclick',
      'contextmenu',
      'keydown',
      'mousemove',
      'mousedown',
      'scroll',
      'touchstart'
    ];
    events.forEach((event) => window.addEventListener(event, updateActivity, { passive: true }));

    // Handle tab visibility change to sync session and tokens immediately
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const storedLastActivity = localStorage.getItem('lastActivity');
        if (storedLastActivity) {
          lastActivityRef.current = parseInt(storedLastActivity, 10);
        } else {
          lastActivityRef.current = now;
          localStorage.setItem('lastActivity', now.toString());
        }
        checkTokenExpiration();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle hover state
    const handleMouseEnter = () => setIsHovering(true);
    const handleMouseLeave = () => setIsHovering(false);
    window.addEventListener('mouseenter', handleMouseEnter);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      events.forEach((event) => window.removeEventListener(event, updateActivity));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('mouseenter', handleMouseEnter);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [checkTokenExpiration]);

  useEffect(() => {
    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 60 * 1000);
    return () => clearInterval(interval);
  }, [checkTokenExpiration]);

  useEffect(() => {
    const SESSION_TIMEOUT = 30 * 60 * 1000;
    const WARNING_TIME = 5 * 60 * 1000;

    // Update last activity on mount to consider opening a tab as activity
    const nowOnMount = Date.now();
    lastActivityRef.current = nowOnMount;
    localStorage.setItem('lastActivity', nowOnMount.toString());

    const checkSessionTimeout = () => {
      const now = Date.now();

      const storedLastActivity = localStorage.getItem('lastActivity');
      const lastActivity = storedLastActivity
        ? Math.max(parseInt(storedLastActivity, 10), lastActivityRef.current)
        : lastActivityRef.current;

      lastActivityRef.current = lastActivity;

      const timeSinceLastActivity = now - lastActivity;
      const remainingTime = SESSION_TIMEOUT - timeSinceLastActivity;

      if (remainingTime <= WARNING_TIME && remainingTime > 0) {
        setTimeLeft(Math.ceil(remainingTime / 1000));
        setShowWarning(true);
      } else if (remainingTime <= 0) {
        localStorage.removeItem('lastActivity');
        clearAllCookies();
        window.location.href = '/login';
      } else {
        setShowWarning(false);
      }
    };

    const sessionCheckInterval = setInterval(checkSessionTimeout, 1000);
    return () => clearInterval(sessionCheckInterval);
  }, []);

  return (
    <div ref={rootRef}>
      {children}
      {getCookie('accessToken') && <Snackbar
        open={showWarning}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setShowWarning(false)}
        autoHideDuration={null}
      >
        <Alert
          severity="info"
          sx={{ width: '100%' }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          Your session will expire in {timeLeft} seconds. {isHovering && 'onClick your mouse to stay logged in!'}
        </Alert>
      </Snackbar>}
    </div>
  );
};

export default SessionManager;